import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { colors } from 'src/theme'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { FundingInvoice } from 'src/interfaces/payment'
import { handleApiError } from 'src/utils/errorHandlers'
import { ResetFundingInvoice, SubmitFundingInvoiceRequest } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch, useSelector } from 'react-redux'
import { formatCurrencyFromCents } from 'src/utils/transformers'
import { isNonNullableArray } from 'src/utils/baseUtils'
import { GSIcon } from 'src/components/shared/GSIcon'
import { getAccessInfo } from 'src/selectors/user'
import { UserProductName } from 'src/constants/user'
import { ModalWithAnimation as LoaderModal } from '../../shared/modalWithAnimation/ModalWithAnimation'
import processingAnimationData from '../../../assets/animations/processing.json'

interface Props {
  isOpen?: boolean
  onBack(): void
  onClose(): void
  onCreateInvoice(): void
  currentInvoice: FundingInvoice | null
  refreshFundingInvoice(): Promise<void>
  onRefreshPayments: () => void
  onRefreshMCPayments: () => void
}

const BankFundConfirmModalComponent = ({
  isOpen,
  onClose,
  onCreateInvoice,
  currentInvoice,
  refreshFundingInvoice,
  onBack,
  onRefreshPayments,
  onRefreshMCPayments,
}: Props) => {
  const dispatch = useDispatch()

  const accessInfo = useSelector(getAccessInfo)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleCreateInvoice = useCallback(async () => {
    if (!currentInvoice) {
      return null
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        SubmitFundingInvoiceRequest.request({ invoiceId: currentInvoice.id }),
      )

      refreshFundingInvoice()
      onRefreshPayments()

      if (accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)) {
        onRefreshMCPayments()
      }

      onCreateInvoice()
      onClose()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [
    onCreateInvoice,
    onClose,
    currentInvoice,
    isLoading,
    onRefreshPayments,
    accessInfo,
    onRefreshMCPayments,
  ])

  const handleResetInvoice = useCallback(async () => {
    if (!currentInvoice) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ResetFundingInvoice.request({ invoiceId: currentInvoice.id }),
      )

      refreshFundingInvoice()

      onBack()
      onClose()
    } catch {
      handleApiError(
        'We couldn’t cancel your invoice request. Please refresh the page or check your internet connection.',
      )
    } finally {
      setIsLoading(false)
    }
  }, [onCreateInvoice, onClose, currentInvoice, isLoading])

  if (isOpen && !isNonNullableArray(currentInvoice?.invoiceIds)) {
    return (
      <LoaderModal
        isOpen={true}
        onCancel={handleResetInvoice}
        title={'Analyzing Your Payments'}
        description={
          'We’re reviewing and organizing your payment data — this may take up to 15 seconds.'
        }
        animationData={processingAnimationData}
      />
    )
  }

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div css={tw`bg-white rounded-lg overflow-hidden w-[474px]`}>
        <ModalHeader css={tw`h-[46px]`} title="Fund payments" showCloseButton={false} />
        <div css={tw`p-4`}>
          <GSIcon iconName="gs_bank" css={tw`mb-2 w-5 h-5 self-center`} />
          <Typography variant="h4" css={tw`mb-1`}>
            Bank transfer
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400}>
            Generate an invoice to transfer funds to our bank account. Once received, we'll send
            payments to your contractors.
          </Typography>
          <div css={tw`grid grid-cols-2 gap-6 w-3/4 my-6`}>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {currentInvoice?.amount && formatCurrencyFromCents(currentInvoice.amount)}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Amount to fund
              </Typography>
            </div>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {currentInvoice?.earningsCount}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Payments
              </Typography>
            </div>
          </div>
          <WarningContainer
            title="Important"
            subtitle="After the invoice is created and while it's awaiting funding, you won't be able to add or modify payments. If you need to make changes, you'll need to cancel the invoice and start the process again."
          />
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-2 mt-6`}>
            To proceed, simply click "Create invoice". An autogenerate invoice will be available for
            your records.
          </Typography>
        </div>
        <div
          css={tw`px-4 py-3 flex flex-row justify-between items-center border-0 border-t border-solid border-[#EDEDED]`}
        >
          <Button size="small" color="grey" onClick={handleResetInvoice} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            size="small"
            color="light"
            onClick={handleCreateInvoice}
            disabled={isLoading}
            loading={!isNonNullableArray(currentInvoice?.invoiceIds)}
          >
            Create invoice
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const BankFundConfirmModal = memo(BankFundConfirmModalComponent)
